/**
 * Fait : les entrées de la barre du site — les tâches, l'aide, la sortie —
 *   et le retour contextuel de la notice. Elles vivent ensemble parce
 *   qu'elles forment UNE barre, et séparément de l'aiguillage parce qu'elles
 *   ne décident d'aucun écran.
 *
 *   Depuis la refonte du 2026-08-28, la barre est LA MÊME sur tous les
 *   écrans : sa composition ne dépend que de l'état de session (l'aiguillage
 *   l'assemble une seule fois), et ses entrées se rangent par tâches —
 *   travailler, régler, s'aider — par un marqueur de groupe purement visuel.
 *
 * Ne fait pas : aucune de ces entrées ne décide d'un droit. Celle de
 *   l'administration ne s'affiche que pour un parent administrateur, mais
 *   cacher un lien n'est pas une protection — l'API refuse de son côté.
 */

import type { ReactNode } from 'react';

import type { Route } from './routes.ts';

/** Ce qu'une entrée de barre sait dire : où elle mène, et si l'on y est. */
interface EntreeDeBarre {
  /** Premier lien de son groupe visuel : un séparateur se dessine avant. */
  readonly debutDeGroupe?: boolean | undefined;

  /** L'écran courant est cette entrée : elle le dit (`aria-current`). */
  readonly courant?: boolean | undefined;
}

/**
 * Où l'on était quand on a ouvert la notice ou la page vie-privée. Retenu
 * dans la session du navigateur, et non en mémoire : ces pages se lisent
 * longtemps, et un rechargement ne doit pas faire oublier d'où l'on vient.
 */
const CLE_DE_RETOUR = 'clannik.retour';

/** L'écran d'où l'on vient, ou la liste des projets si on ne sait pas. */
export function origineDuRetour(): string {
  try {
    const retenu = sessionStorage.getItem(CLE_DE_RETOUR);
    return retenu !== null && retenu.startsWith('#/') ? retenu : '#/projets';
  } catch {
    // Sans stockage de session, on retombe sur la liste : dégradé, pas cassé.
    return '#/projets';
  }
}

/** Retient l'écran courant, pour que le retour sache où ramener. */
function retiensLOrigine(): void {
  try {
    sessionStorage.setItem(CLE_DE_RETOUR, location.hash === '' ? '#/projets' : location.hash);
  } catch {
    // Rien à faire : le retour retombera sur la liste des projets.
  }
}

/** Le `li` commun : son groupe, et rien d'autre. */
function Entree({ debutDeGroupe, children }: EntreeDeBarre & { readonly children: ReactNode }) {
  return <li className={debutDeGroupe === true ? 'debut-de-groupe' : undefined}>{children}</li>;
}

/** `aria-current` ne se pose que vrai : `false` s'écrirait dans le HTML. */
const courantOuRien = (courant: boolean | undefined) => (courant === true ? 'page' : undefined);

/**
 * Le lien vers la notice, présent sur TOUS les écrans, y compris celui de la
 * connexion. Une aide qu'il faut d'abord réussir à entrer pour trouver ne sert
 * personne.
 *
 * Il RETIENT l'écran quitté : sans cela, le retour de la notice ramenait
 * toujours à l'accueil, et l'on perdait la conversation qu'on était en train
 * de lire — défaut trouvé par Charles en cinq clics, le 2026-08-04.
 */
export function LienNotice({ debutDeGroupe, courant }: EntreeDeBarre) {
  return (
    <Entree debutDeGroupe={debutDeGroupe}>
      <a
        href="#/notice"
        data-role="vers-la-notice"
        aria-current={courantOuRien(courant)}
        onClick={retiensLOrigine}
      >
        Comment ça marche
      </a>
    </Entree>
  );
}

/**
 * L'entrée vers « Ce que Clannik voit ». Elle est présente PARTOUT, connecté ou
 * non — `D-033` : personne ne doit écrire dans Clannik en croyant à un secret
 * absolu, et une explication qu'il faut d'abord réussir à entrer pour trouver
 * arriverait après le moment où elle sert.
 *
 * Elle RETIENT l'écran quitté, comme la notice — forme voisine attrapée par
 * l'épreuve de la barre unique : le retour existait, mais il ramenait à
 * l'origine de la DERNIÈRE visite de notice, pas à l'écran qu'on venait de
 * quitter.
 */
export function LienVieePrivee({ courant }: EntreeDeBarre) {
  return (
    <Entree>
      <a
        href="#/vie-privee"
        data-role="vers-la-vie-privee"
        aria-current={courantOuRien(courant)}
        onClick={retiensLOrigine}
      >
        Ce que Clannik voit
      </a>
    </Entree>
  );
}

/**
 * Le groupe S'AIDER — la notice puis la vie privée, dans cet ordre : un
 * témoin exige d'atteindre un lien en deux tabulations depuis le haut de la
 * notice, et la notice reste la première aide qu'on cherche.
 */
export function CommandesCommunes({
  debutDeGroupe,
  ecran,
}: {
  readonly debutDeGroupe?: boolean | undefined;
  readonly ecran?: Route['ecran'] | undefined;
}) {
  return (
    <>
      <LienNotice debutDeGroupe={debutDeGroupe} courant={ecran === 'notice'} />
      <LienVieePrivee courant={ecran === 'vie-privee'} />
    </>
  );
}

/**
 * Le retour depuis la notice ou la vie privée, vers l'écran d'où l'on est
 * parti. C'est une commande DE PAGE — elle agit sur la lecture en cours —
 * et elle vit donc dans le contenu, plus dans la barre : la barre, elle, ne
 * change jamais.
 */
export function RetourALAtelier() {
  return (
    <p className="retour-a-l-atelier">
      <a href={origineDuRetour()} data-role="vers-l-atelier">
        Revenir à l’atelier
      </a>
    </p>
  );
}

/**
 * L'entrée vers l'administration de la famille. Elle n'apparaît que pour un
 * parent administrateur — et c'est le SERVEUR qui l'a dit, par le rôle du
 * profil. Cacher le lien n'est pas une protection : l'écran, lui, ne reçoit
 * rien si l'API refuse.
 */
export function LienAdministration({ courant }: EntreeDeBarre) {
  return (
    <Entree>
      <a
        href="#/administration"
        data-role="vers-l-administration"
        aria-current={courantOuRien(courant)}
      >
        La famille
      </a>
    </Entree>
  );
}

/**
 * L'entrée vers son propre compte. Un LIEN, pas un menu : à fort zoom, un
 * panneau qui se déplie cache le contenu et se referme quand on ne veut pas,
 * tandis qu'un lien ne bouge pas et porte une adresse qu'on met en favori.
 */
export function LienMonCompte({ courant }: EntreeDeBarre) {
  return (
    <Entree>
      <a href="#/compte" data-role="vers-mon-compte" aria-current={courantOuRien(courant)}>
        Mon compte
      </a>
    </Entree>
  );
}

/**
 * L'entrée vers la bibliothèque de consignes. Un LIEN, pour la même raison que
 * « Mon compte » : il ne bouge pas, il porte une adresse qu'on met en favori,
 * et le contrôle des portes a un écran nommé à pointer.
 *
 * Elle est offerte à TOUS, sans condition de rôle : la bibliothèque est
 * commune depuis `D-025`, et chacun peut y écrire la sienne.
 */
export function LienConsignes({ courant }: EntreeDeBarre) {
  return (
    <Entree>
      <a href="#/consignes" data-role="vers-les-consignes" aria-current={courantOuRien(courant)}>
        Consignes
      </a>
    </Entree>
  );
}

/**
 * LE RETOUR CHEZ SOI — la liste des projets, qui est l'accueil du produit.
 *
 * IL A MANQUÉ, et c'était le plus gros trou du produit : depuis le lot 1, la
 * barre portait tout sauf un chemin vers l'accueil. Trouvé par Charles le
 * 2026-08-27 (« comment est-ce qu'on revient à la liste des projets ? ») —
 * et raté par 168 vérifications, parce qu'elles se déplaçaient en posant
 * l'adresse au lieu de cliquer.
 *
 * IL EST LE PREMIER DE LA BARRE, et c'est délibéré : c'est le lien du retour,
 * celui qu'on cherche quand on est perdu, et on le cherche au début.
 */
export function LienProjets({ courant }: EntreeDeBarre) {
  return (
    <Entree>
      <a href="#/projets" data-role="vers-les-projets" aria-current={courantOuRien(courant)}>
        Mes projets
      </a>
    </Entree>
  );
}

/**
 * L'entrée vers l'argent — ce qu'on peut dépenser, et ce qu'on a dépensé.
 *
 * Elle est offerte à TOUS, et non au seul parent administrateur. Un plafond
 * invisible est la pire des explications quand une demande ne part pas : la
 * personne croit à une panne du produit. Chacun doit pouvoir aller voir. Ce
 * qu'un membre ordinaire y trouve est réduit d'office — le serveur ne lui rend
 * ni le tableau familial ni les réglages, et refuse les routes qui écrivent.
 */
export function LienArgent({ debutDeGroupe, courant }: EntreeDeBarre) {
  return (
    <Entree debutDeGroupe={debutDeGroupe}>
      <a href="#/argent" data-role="vers-l-argent" aria-current={courantOuRien(courant)}>
        L’argent
      </a>
    </Entree>
  );
}

/**
 * Sortir. Un bouton, jamais un lien : ce geste appelle le serveur, il ne
 * navigue pas. Et il NE SE CONFIRME PAS — se déconnecter n'est pas destructif,
 * on se reconnecte. La fenêtre de confirmation est réservée à l'irréversible,
 * sinon elle s'use et ne protège plus rien.
 *
 * Il rend le bouton NU : le gabarit de page le place en haut à droite du
 * bandeau, dernier du parcours clavier — l'ordre du document ne ment pas.
 */
export function BoutonDeSortie({ surSortie }: { readonly surSortie: () => void }) {
  return (
    <button type="button" data-role="se-deconnecter" onClick={surSortie}>
      Se déconnecter
    </button>
  );
}
